import { type ReactNode } from 'react';
import {
  type DrawerMenuItem,
  type NavItemNoIcon,
  navItemType,
  type OddsFormat,
  type OddsFormatUppercase,
} from '@cx-web/domain-types';
import classnames from 'classnames';

import { type Repub } from '@cx-modules/constants';

import OddsFormatDropdown from '../OddsFormatDropdown/index';
import OddsFormatDropdownLegacy from '../OddsFormatDropdownLegacy/index';
import StateDropdown from '../StateDropdown/index';
import ToolsDropdown from '../ToolsDropdown/index';

import styles from './DesktopConfigItems.module.css';

type DesktopConfigItemsProps = {
  betslipSlot?: ReactNode;
  isLoggedIn: boolean;
  // When false (default): legacy OddsFormatDropdownLegacy + legacy StateDropdown + original gap.
  // When true: new UK style (OddsFormatDropdown with icon, new StateDropdown, smaller gap).
  isUkStyle?: boolean;
  links: NavItemNoIcon[];
  logoutLabel: string;
  mobileHamburgerNav: DrawerMenuItem[];
  oddsFormat: OddsFormat;
  oddsFormatOptions?: OddsFormatUppercase[];
  onLogout: () => void;
  onOddsFormatChange: ((format: OddsFormat) => void) | null;
  onStateChange?: (state: string) => void;
  renderAccountMenu?: ((variant: 'desktop' | 'mobile') => ReactNode) | null;
  repub?: Repub;
  selectedState?: string;
  shouldRenderOddsFormatSelector: boolean;
  stateOptions?: Array<{ code: string; label: string }>;
};

const filterSingleStateSelector = (links: NavItemNoIcon[]) => {
  return links.filter((link, index) => {
    if (link.type !== navItemType.STATE_SELECTOR) return true;

    const firstSelectorIndex = links.findIndex(l => l.type === navItemType.STATE_SELECTOR);
    return index === firstSelectorIndex;
  });
};

const DesktopConfigItems = ({
  betslipSlot,
  isLoggedIn,
  isUkStyle = false,
  links,
  logoutLabel,
  mobileHamburgerNav,
  oddsFormat,
  oddsFormatOptions,
  onLogout,
  onOddsFormatChange,
  onStateChange,
  renderAccountMenu,
  repub,
  selectedState,
  shouldRenderOddsFormatSelector,
  stateOptions,
}: DesktopConfigItemsProps) => {
  const filteredLinks = filterSingleStateSelector(links);

  const componentMap = {
    ODDS_FORMAT:
      shouldRenderOddsFormatSelector && onOddsFormatChange
        ? isUkStyle
          ? () => (
              <OddsFormatDropdown
                oddsFormat={oddsFormat}
                oddsFormatOptions={oddsFormatOptions}
                onOddsFormatChange={onOddsFormatChange}
                repub={repub}
              />
            )
          : () => <OddsFormatDropdownLegacy oddsFormat={oddsFormat} onOddsFormatChange={onOddsFormatChange} />
        : null,
    MY_ACCOUNT: renderAccountMenu ? () => renderAccountMenu('desktop') : null,
    BETSLIP: betslipSlot ? () => <div className={styles.hideOnMobile}>{betslipSlot}</div> : null,
    SETTINGS: () => {
      const toolsItem = links.find(link => link.type === navItemType.SETTINGS);
      return toolsItem?.subLinks?.length ? (
        <ToolsDropdown
          iconSize={isUkStyle ? 18 : 16}
          isLoggedIn={isLoggedIn}
          items={toolsItem.subLinks}
          legacyToggle={!isUkStyle}
          logoutLabel={logoutLabel}
          onLogout={onLogout}
        />
      ) : null;
    },
    STATE_SELECTOR: () => (
      <StateDropdown
        firstLevelNav={links}
        isUkStyle={isUkStyle}
        mobileHamburgerNav={mobileHamburgerNav}
        onStateChange={onStateChange}
        selectedState={selectedState}
        stateOptions={stateOptions}
      />
    ),
  };

  return (
    <div
      className={classnames(styles.itemsWrapper, isUkStyle ? styles.ukGap : styles.legacyGap)}
      data-testid="desktop-config-items"
    >
      {filteredLinks.map((link, index) => {
        const Component = componentMap[link.type as keyof typeof componentMap];
        return Component && <Component key={link.name || index} />;
      })}
    </div>
  );
};

export { DesktopConfigItems };
